import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AlertTriangle, X } from 'lucide-react';
import useWorkoutPlayer from '../components/WorkoutPlayer/useWorkoutPlayer';
import useRestTimer from '../components/WorkoutPlayer/useRestTimer';
import TodayScreen from '../components/WorkoutPlayer/TodayScreen';
import ExerciseScreen from '../components/WorkoutPlayer/ExerciseScreen';
import ExerciseListSheet from '../components/WorkoutPlayer/ExerciseListSheet';
import SubstituteSheet from '../components/WorkoutPlayer/SubstituteSheet';
import AddExerciseSheet from '../components/WorkoutPlayer/AddExerciseSheet';
import ExerciseHistorySheet from '../components/WorkoutPlayer/ExerciseHistorySheet';
import DemoOverlay from '../components/WorkoutPlayer/DemoOverlay';
import FinishScreen from '../components/WorkoutPlayer/FinishScreen';
import { ConfirmDialog, ErrorState, LoadingState } from '../components/WorkoutPlayer/parts';
import { resolveDemo } from '../components/WorkoutPlayer/demoRegistry';
import { useAuth } from '../contexts/AuthContext';
import { firstOpenIndex, hasAllSets, plannedSetCount, progressOf, restAfterSet, setStorageScope, whyText } from '../components/WorkoutPlayer/playerLogic';
import '../components/WorkoutPlayer/workoutPlayer.css';

// O player de treino do sócio ("O meu treino de hoje"). Mobile-first; em ecrã largo reorganiza-se em duas colunas.
// Usa os endpoints reais de api/training (Bloco 3): nada aqui inventa estado — mostra o que o servidor tem e o que ainda está na fila local.

/** Mantém o ecrã ligado durante o treino, se o navegador deixar. */
function useWakeLock(active) {
  useEffect(() => {
    if (!active || typeof navigator === 'undefined' || !navigator.wakeLock) return undefined;
    let lock = null;
    let cancelled = false;
    const acquire = async () => {
      try { lock = await navigator.wakeLock.request('screen'); if (cancelled && lock) lock.release(); } catch { /* sem wake lock: o ecrã apaga-se como sempre */ }
    };
    acquire();
    const onVisible = () => { if (document.visibilityState === 'visible') acquire(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => { cancelled = true; document.removeEventListener('visibilitychange', onVisible); try { lock?.release(); } catch { /* idem */ } };
  }, [active]);
}

/** O player propriamente dito: só existe durante um treino em curso (o descanso é restaurado ao montar). */
function PlayerStage({ wp, onLeave }) {
  const { view, index, setIndex } = wp;
  const executionId = view.executionId;
  const timer = useRestTimer(String(executionId));
  const [ui, setUi] = useState({ list: false, demo: false, substitute: false, add: false, history: false, skip: false, finish: false, abandon: false });
  const [dialogError, setDialogError] = useState(null);
  const open = (key) => setUi((u) => ({ ...u, [key]: true }));
  const close = (key) => { setUi((u) => ({ ...u, [key]: false })); setDialogError(null); };

  useWakeLock(true);

  const exercises = view.exercises;
  const exercise = exercises[Math.min(index, exercises.length - 1)];
  const progress = useMemo(() => progressOf(exercises), [exercises]);
  const demo = useMemo(() => resolveDemo(exercise), [exercise]);

  const go = useCallback((target) => {
    const i = Math.max(0, Math.min(exercises.length - 1, target));
    setIndex(i);
  }, [exercises.length, setIndex]);

  const onLog = useCallback((draft) => {
    const result = wp.logSet(index, draft);
    // O aquecimento não abre o cronómetro: não é uma série de trabalho. Num superset o descanso é entre rondas
    // (só depois do último do grupo), nunca entre A1 e A2.
    if (result.ok && !draft.isWarmup) {
      const rest = restAfterSet(exercises, index, result.setIndex);
      const isFinal = index === exercises.length - 1 && result.setIndex >= plannedSetCount(exercise);
      if (rest > 0 && !isFinal) timer.start(rest);
    }
    return result;
  }, [wp, index, exercise, exercises, timer]);

  const onNext = useCallback(() => {
    // Todas as séries previstas feitas: o exercício fecha-se sozinho ao avançar (sem mais um botão).
    if (hasAllSets(exercise) && exercise.status !== 'Completed' && exercise.status !== 'Skipped') wp.completeExercise(index);
    go(index + 1);
  }, [exercise, go, index, wp]);

  const onSkip = useCallback((reason) => {
    wp.skipExercise(index, reason);
    close('skip');
    const next = firstOpenIndex(exercises.map((e, i) => (i === index ? { ...e, status: 'Skipped' } : e)), index + 1);
    go(next >= 0 ? next : Math.min(index + 1, exercises.length - 1));
  }, [exercises, go, index, wp]);

  const onSubstitute = useCallback(async (libraryExerciseId, reason) => {
    const result = await wp.substitute(index, libraryExerciseId, reason);
    if (result.ok) close('substitute');
    return result;
  }, [index, wp]);

  // «Como foi este exercício?»: um toque. Não bloqueia — a navegação continua sempre disponível.
  const onFeedback = useCallback((verdict) => wp.sendFeedback(index, verdict), [index, wp]);

  // Acrescentar ao treino um exercício que o plano não trazia. Ao entrar, leva-o lá: o novo ficou no fim.
  const onAddExercise = useCallback(async (libraryExerciseId) => {
    const result = await wp.addExercise(libraryExerciseId);
    if (result.ok) {
      close('add');
      if (Number.isInteger(result.index) && result.index >= 0) wp.setIndex(result.index);
    }
    return result;
  }, [wp]);

  // Corrigir ou apagar uma série já enviada ('delete' apaga). Só com ligação; o resultado diz porquê quando não deu.
  const onEditSet = useCallback((set, change) => wp.editSet(index, set, change), [index, wp]);

  const onFinish = useCallback(async () => {
    const result = await wp.finish();
    if (!result.ok) setDialogError(result.message);
  }, [wp]);

  const onAbandon = useCallback(async (reason) => {
    const result = await wp.abandon(reason);
    if (!result.ok) setDialogError(result.message);
  }, [wp]);

  const skipped = progress.skipped;
  const remaining = progress.remaining;

  return (
    <>
      <ExerciseScreen
        view={view} index={index} timer={timer} sync={wp.sync} pendingCount={wp.pendingCount} busy={wp.busy}
        onExit={onLeave} onOpenList={() => open('list')} onOpenDemo={() => open('demo')} onOpenSubstitute={() => open('substitute')}
        onOpenHistory={() => open('history')} onEditSet={onEditSet}
        onPrev={() => go(index - 1)} onNext={onNext} onJump={go} onLog={onLog}
        onSkipRequest={() => open('skip')} onFinishRequest={() => open('finish')} onFeedback={onFeedback}
      />

      <ExerciseListSheet open={ui.list} exercises={exercises} index={index} onJump={go} onClose={() => close('list')}
        onAdd={() => { close('list'); open('add'); }} canAdd={!wp.busy.addExercise}
        canFinish={progress.setsLogged > 0} onFinish={() => { close('list'); open('finish'); }} onAbandon={() => { close('list'); open('abandon'); }} />

      <DemoOverlay open={ui.demo} demo={demo} onClose={() => close('demo')} />

      <ExerciseHistorySheet open={ui.history} exercise={exercise} onClose={() => close('history')} />

      <SubstituteSheet open={ui.substitute} exercise={exercise} busy={!!wp.busy.substitute} onClose={() => close('substitute')} onConfirm={onSubstitute}
        inSessionIds={exercises.map((e) => e.executedLibraryExerciseId ?? e.plannedLibraryExerciseId).filter(Boolean)} />

      <AddExerciseSheet open={ui.add} busy={!!wp.busy.addExercise} onClose={() => close('add')} onAdd={onAddExercise}
        inSessionIds={exercises.map((e) => e.executedLibraryExerciseId ?? e.plannedLibraryExerciseId).filter(Boolean)} />

      <ConfirmDialog open={ui.skip} title="Saltar este exercício?" confirmLabel="Saltar exercício" cancelLabel="Voltar" reasonLabel="Motivo" onCancel={() => close('skip')} onConfirm={onSkip}>
        <p><strong>{exercise.name}</strong> fica de fora deste treino. O seu plano não muda.</p>
      </ConfirmDialog>

      <ConfirmDialog open={ui.finish} title="Concluir o treino?" confirmLabel="Concluir treino" busy={!!wp.busy.finish} onCancel={() => close('finish')} onConfirm={onFinish}>
        <p>{progress.setsLogged} série{progress.setsLogged === 1 ? '' : 's'} registada{progress.setsLogged === 1 ? '' : 's'} · {progress.completed} exercício{progress.completed === 1 ? '' : 's'} concluído{progress.completed === 1 ? '' : 's'}.</p>
        {remaining > 0 ? <p className="wp-warn-text"><AlertTriangle size={16} aria-hidden="true" /> Faltam {remaining} exercício{remaining === 1 ? '' : 's'}: os que não tiverem séries ficam como saltados.</p> : null}
        {skipped > 0 ? <p className="wp-muted">{skipped} saltado{skipped === 1 ? '' : 's'}.</p> : null}
        {wp.pendingCount > 0 ? <p className="wp-warn-text">Há {wp.pendingCount} registo(s) por enviar; vão ser enviados antes de concluir.</p> : null}
        {dialogError ? <p className="wp-error" role="alert" data-testid="dialog-error">{dialogError}</p> : null}
      </ConfirmDialog>

      <ConfirmDialog open={ui.abandon} title="Abandonar o treino?" danger confirmLabel="Abandonar treino" busy={!!wp.busy.abandon} reasonLabel="Motivo" onCancel={() => close('abandon')} onConfirm={onAbandon}>
        <p>O treino fica registado como abandonado, {progress.setsLogged === 1 ? 'com a série que já fez' : `com as ${progress.setsLogged} séries que já fez`}. Não ganha XP e não pode voltar a este treino.</p>
        <p className="wp-muted">Se só quer fazer uma pausa, use <strong>✕</strong> no topo: o treino fica em curso e retoma-o quando quiser.</p>
        {dialogError ? <p className="wp-error" role="alert">{dialogError}</p> : null}
      </ConfirmDialog>
    </>
  );
}

export default function WorkoutPlayerPage() {
  const nav = useNavigate();
  // O que o player guarda no aparelho é de quem treinou (ver setStorageScope): definido ANTES de o hook ler o que lá ficou.
  const { user } = useAuth();
  setStorageScope(user?.id);
  const wp = useWorkoutPlayer();
  const [abandonFromToday, setAbandonFromToday] = useState(false);
  const [abandonError, setAbandonError] = useState(null);
  const goHome = () => nav('/meu-treino');   // o treino tem a sua página (Hoje · Plano · Histórico); o Início é a visão geral

  // O hub do Treino (Hoje · Plano · Histórico) é o ecrã de "hoje": "Começar" / "Continuar" chegam aqui já com a intenção e o treino arranca sem um segundo "Começar".
  // Quem abre /treino diretamente (favorito, atalho) vê o ecrã de hoje do próprio player.
  const location = useLocation();
  const intent = useRef(location.state && location.state.start ? { dayId: location.state.dayId ?? null, why: location.state.why ?? null } : null);
  const handled = useRef(false);
  const { phase, active, start, resume } = wp;
  useEffect(() => {
    if (!intent.current || handled.current || phase !== 'today') return;
    handled.current = true;
    if (active) resume(); else start(intent.current.dayId);
  }, [phase, active, start, resume]);
  const autoStarting = !!intent.current && phase === 'today' && (!handled.current || !!wp.busy.start);

  const leaveToHub = () => { wp.toToday(); goHome(); };

  // A razão de o dia ter sido sugerido. Vem do ecrã de onde se partiu (o atalho «Começar» salta o ecrã
  // de hoje) ou do próprio pedido de hoje; dentro da sessão é a única altura em que o atleta a vê.
  const reason = whyText(intent.current?.why) || whyText(wp.today?.suggestedBy);

  let body;
  if (wp.phase === 'loading' || autoStarting) body = <div className="wp-screen"><LoadingState /></div>;
  else if (wp.phase === 'error') body = <div className="wp-screen"><ErrorState message={wp.error} onRetry={wp.load} onBack={goHome} /></div>;
  else if (wp.phase === 'finished' && wp.finished) body = <FinishScreen summary={wp.finished} onDone={() => { wp.backToToday(); goHome(); }} onProgress={() => { wp.backToToday(); nav('/progress'); }} />;
  else if (wp.phase === 'player' && wp.view) body = (
    <>
      {reason ? (
        <p className="wp-muted wp-why" role="status" data-testid="session-why" style={{ padding: '0 16px', margin: '4px 0' }}>
          {reason}
        </p>
      ) : null}
      <PlayerStage wp={wp} onLeave={leaveToHub} />
    </>
  );
  else {
    body = (
      <>
        <TodayScreen today={wp.active ? wp.view : wp.today} active={wp.active} view={wp.view} starting={!!wp.busy.start} startError={wp.error}
          onStart={wp.start} onResume={wp.resume} onBack={goHome} onAbandon={() => setAbandonFromToday(true)} />
        <ConfirmDialog open={abandonFromToday} title="Abandonar o treino?" danger confirmLabel="Abandonar treino" busy={!!wp.busy.abandon} reasonLabel="Motivo"
          onCancel={() => { setAbandonFromToday(false); setAbandonError(null); }}
          onConfirm={async (reason) => { const r = await wp.abandon(reason); if (r.ok) setAbandonFromToday(false); else setAbandonError(r.message); }}>
          <p>O treino fica registado como abandonado, com as séries que já fez. Não ganha XP.</p>
          {abandonError ? <p className="wp-error" role="alert">{abandonError}</p> : null}
        </ConfirmDialog>
      </>
    );
  }

  return (
    <div className="wp-root" data-testid="workout-player" data-phase={wp.phase}>
      {wp.failedOps.length > 0 ? (
        <div className="wp-banner wp-banner-bad" role="alert">
          <AlertTriangle size={18} aria-hidden="true" />
          <span>{wp.failedOps[0].message}</span>
          <button type="button" className="wp-icon-btn" onClick={() => wp.dismissFailed(wp.failedOps[0].id)} aria-label="Fechar aviso"><X size={18} aria-hidden="true" /></button>
        </div>
      ) : null}
      {body}
    </div>
  );
}
